import { reactive, computed } from '@vue/composition-api'
import store from '@/store'
import * as apiCommon from '@/api/common'
import * as apiPrecedent from '@/api/precedent'
import * as apiBookmark from '@/api/bookmark'
import * as _ from 'lodash'
import * as common from '@/util/common'
import Vue from 'vue'

export const Document = (props, context) => {
  const router = context.root.$router

  const documentLogic = reactive({
    // 先例・判例共通state
    state: {
      // 書籍セレクタ
      books: computed(() => store.state.precedent.books),
      // タブ状態 1：検索タブ、2：目次タブ
      tab: computed(() => store.state.precedent.tab),
      // 書籍ID
      bookId: computed(() => store.state.precedent.bookId),
      // 書籍リビジョンID
      bookRevisionId: computed(() => store.state.precedent.bookRevisionId),
      // 書籍名
      bookName: computed(() => store.state.precedent.bookName),
      // 検索タイトル
      subTitle: computed(() => store.state.precedent.subTitle),
      // テーマID
      themeId: computed(() => store.state.precedent.themeId),
      // テーマ名
      themeName: computed(() => store.state.precedent.themeName),
      // 詳細ID
      detailId: computed(() => store.state.precedent.detailId),
      // 詳細名
      detailName: computed(() => store.state.precedent.detailName),
      // フロントエンドログインフラグ（管理システムから遷移してきた場合）
      isFrontendLogin: computed(() => store.state.profile.login_type === 2),

      // 共通state
      isShowResult: false,
      isShowDetail: false,
      visibleMenuLeft: false, // SP用左メニュー表示フラグ
      visibleNoteSelect: false, // ノート選択モーダル用フラグ
      visibleNoteCreate: false, // SPノート作成モーダル用フラグ
      isFirst: true, // API初回キックフラグ
      isRevisionFixed: false, // リビジョンIDが確定したらtrueにする
      // 目次のみstate
      isFullSummary: false,
      isSmallSummary: false,
      // 検索のみstate
      isSmallForm: false,
      isFilteredTheme: false,
      visibleSearchOrderSettings: false,
      visibleSearchDisplaySettings: false,
      visibleSaibansyoSettings: false,
      isBookChanged: false,

      // 各種ローディング
      isBooksLoading: true,
      isMasterLoading: true,
      isTocLoading: true,
      isListLoading: false,
      isDetailLoading: true
    },

    data: reactive({
      // 書籍分類データ
      bookTypes: [],
      // 発翰名データ
      hakkan: [],
      // 裁判所データ
      saibansyo: [],
      // 出典データ
      syuttenn: [],
      // サマリーに表示するURL
      summaryUrl: '',

      // 目次のテーマデータor検索の一覧データ(theme,related)
      list: [],
      // 詳細表示データ
      detail: {}
    }),

    // 先例・判例共通init
    init: () => {
      documentLogic.checkResizeResult()
      documentLogic.getSummaryUrl()
    },

    // ヘッダー書籍セレクタのデータ取得・選択書籍のリビジョンID更新
    getBooks: () => {
      const params = {}
      apiPrecedent.getBook(params)
        .then(response => {
          const { data } = response
          if (data.status === 0 && data.books.length >= 1) {
            store.dispatch('precedent/setBooks', _.sortBy(data.books, 'book_id'))
          } else if (data.books.length === 0) {
            // エラー処理
            // 書籍データがない場合、エラーを表示した後画面を閉じる
            const payload = {
              title: 'エラー',
              text: '資料検索を利用できなくなりました。',
              button1: {
                action: () => { window.close() }
              },
              buttonClose: {
                action: () => { window.close() }
              }
            }
            store.dispatch('common/showNotice', payload)
            store.dispatch('precedent/clearAll')
          } else {
            // エラー処理
            documentLogic.showDocumentError(data.error_message)
          }
        })
        .finally(() => {
          documentLogic.state.isBooksLoading = false
        })
    },

    // マスタ取得APIから分類情報を取得
    getTypesData: (previewBookRevisionId) => {
      return new Promise((resolve, reject) => {
        const masterParams = {
          key: 'SENREI_HANREI_DAITYPE',
          value1: String(documentLogic.state.bookRevisionId)
        }
        if (previewBookRevisionId !== 0) {
          masterParams.value1 = String(previewBookRevisionId)
        }

        apiCommon.fetchMaster(masterParams)
          .then(response => {
            if (response) {
              const { data } = response
              documentLogic.data.bookTypes = data.list
              resolve()
            }
          })
      })
    },

    // マスタ取得APIから発翰名情報を取得
    getHakkanData: (previewBookRevisionId) => {
      return new Promise((resolve, reject) => {
        const masterParams = {
          key: 'SENREI_HANREI_HAKKAN',
          value1: String(documentLogic.state.bookRevisionId)
        }
        if (previewBookRevisionId !== 0) {
          masterParams.value1 = String(previewBookRevisionId)
        }

        apiCommon.fetchMaster(masterParams)
          .then(response => {
            if (response) {
              const { data } = response
              documentLogic.data.hakkan = data.list
              resolve()
            }
          })
      })
    },

    // マスタ取得APIから裁判所情報を取得
    getSaibansyoData: (previewBookRevisionId) => {
      return new Promise((resolve, reject) => {
        const masterParams = {
          key: 'SENREI_HANREI_SAIBANSYO',
          value1: String(documentLogic.state.bookRevisionId)
        }
        if (previewBookRevisionId !== 0) {
          masterParams.value1 = String(previewBookRevisionId)
        }

        apiCommon.fetchMaster(masterParams)
          .then(response => {
            if (response) {
              const { data } = response
              documentLogic.data.saibansyo = data.list
              resolve()
            }
          })
      })
    },

    // マスタ取得APIから裁判所情報を取得
    getSyuttennData: (previewBookRevisionId) => {
      return new Promise((resolve, reject) => {
        const masterParams = {
          key: 'SENREI_HANREI_SYUTTENN',
          value1: String(documentLogic.state.bookRevisionId)
        }
        if (previewBookRevisionId !== 0) {
          masterParams.value1 = String(previewBookRevisionId)
        }

        apiCommon.fetchMaster(masterParams)
          .then(response => {
            if (response) {
              const { data } = response
              documentLogic.data.syuttenn = data.list
              resolve()
            }
          })
      })
    },

    // マスタ取得APIから各種データを取得
    // プレビュー時だけpreviewBookRevisionIdが渡ってくる
    getMasterData: (previewBookRevisionId = 0) => {
      documentLogic.state.isMasterLoading = true

      // 各処理のPromiseを作成する
      const typesPromise = documentLogic.getTypesData(previewBookRevisionId)
      const hakkanPromise = documentLogic.getHakkanData(previewBookRevisionId)
      const saibansyoPromise = documentLogic.getSaibansyoData(previewBookRevisionId)
      const syuttennPromise = documentLogic.getSyuttennData(previewBookRevisionId)

      // 各種データが更新された後に表示データを再加工
      Promise.all([typesPromise, hakkanPromise, saibansyoPromise, syuttennPromise])
        .then(() => {
          if (documentLogic.data.list.length !== 0) {
            documentLogic.data.list = updatePrecedentDataWithMaster(documentLogic.data.list)
          }
          if (documentLogic.data.detail.id) {
            documentLogic.data.detail = updatePrecedentDataWithMaster([documentLogic.data.detail])[0]
          }
        })
        .finally(() => {
          documentLogic.state.isMasterLoading = false
        })
    },

    getSummaryUrl: async() => {
      // SPは固定文言を表示する場所がないが、念の為取得しておく
      const key = store.state.common.device === 'sp' ? 'URL_GUIDE_PJ_SP' : 'URL_GUIDE_PJ'
      const masterParams = { key }

      const url = await apiCommon.fetchMaster(masterParams)
        .then(response => {
          if (response) {
            const { data } = response
            return data.list[0].value1
          }
        })

      documentLogic.data.summaryUrl = url
    },

    showDetail: reactive({
      open: (detailId) => {
        // 表示エリアの調整
        documentLogic.state.isShowDetail = true
        documentLogic.state.isSmallSummary = true
        // URLの変更
        documentLogic.changeDetailURL(detailId)

        if (typeof context.refs.scrollArea !== 'undefined') {
          setTimeout(context.refs.scrollArea.scroll({ top: 0, behavior: 'smooth' }), 500)
        }
      },
      showSummary: () => {
        if (!documentLogic.state.isShowDetail) return
        documentLogic.state.isSmallSummary = false
      },
      showResult: () => {
        if (!documentLogic.state.isShowDetail) return
        documentLogic.state.isSmallSummary = true
        documentLogic.state.isSmallForm = true

        // 目次タブの詳細ページから要旨一覧を押した場合
        if (documentLogic.state.tab === 2 && documentLogic.data.list.length === 0) {
          documentLogic.state.isShowResult = true
          getPrecedentDetail([documentLogic.state.themeId], 'theme')
        }
      },
      showForm: () => {
        if (!documentLogic.state.isShowDetail) return
        documentLogic.state.isSmallForm = false
      },
      // 関連要旨取得
      openRelated: (index) => {
        Vue.set(documentLogic.data.list[index], 'connection', 'related')
        getPrecedentDetail(documentLogic.data.list[index].related, 'related', index)
      },
      // 参照取得
      openReference: (index) => {
        Vue.set(documentLogic.data.list[index], 'connection', 'reference')
        getPrecedentDetail(documentLogic.data.list[index].reference, 'reference', index)
      }
    }),

    // ヘッダーセレクタの本の選択
    changeBook: () => {
      // リビジョンID指定なしを吸収
      if (store.state.precedent.bookRevisionId === 0) {
        store.dispatch('precedent/setBookRevisionId', documentLogic.state.books[0].book_revision_id)
      }

      const result = _.find(documentLogic.state.books, { book_revision_id: store.state.precedent.bookRevisionId})
      // リビジョンIDがマッチするものを選択
      if (result) {
        store.dispatch('precedent/setBookId', result.book_id)
        store.dispatch('precedent/setBookName', result.name)
        store.dispatch('precedent/setSubTitle', result.sub_title)

      // bookIDがマッチするものを選択
      } else {
        const result2 = _.find(documentLogic.state.books, { book_id: store.state.precedent.bookId})
        if (result2) {
          store.dispatch('precedent/setBookRevisionId', result2.book_revision_id)
          store.dispatch('precedent/setBookName', result2.name)
          store.dispatch('precedent/setSubTitle', result2.sub_title)
        } else {
          // それでもマッチしない場合は一番上の書籍
          store.dispatch('precedent/setBookId', documentLogic.state.books[0].book_id)
          store.dispatch('precedent/setBookRevisionId', documentLogic.state.books[0].book_revision_id)
          store.dispatch('precedent/setBookName', documentLogic.state.books[0].name)
          store.dispatch('precedent/setSubTitle', documentLogic.state.books[0].sub_title)
        }
      }
      documentLogic.state.isRevisionFixed = true
      documentLogic.getMasterData()
    },

    // ヘッダーセレクタの本の選択
    selectBook: (bookRevisionId) => {
      store.dispatch('precedent/setBookRevisionId', bookRevisionId)
      documentLogic.changeBook()

      // storeの情報リセット
      store.dispatch('precedent/clearTheme')
      store.dispatch('precedent/clearDetail')

      // 各種表示もリセット
      documentLogic.state.isShowResult = false
      documentLogic.state.isShowDetail = false
      documentLogic.state.list = []
      documentLogic.state.related = []
      documentLogic.state.detail = {}

      // 書籍が変わったらフラグを立てる
      documentLogic.state.isBookChanged = true

      // ページ移動させる
      const routerParams = {}
      if (documentLogic.state.tab === 1) {
        if (store.state.common.device === 'sp') {
          routerParams.name = 'SPDocumentSearch'
        } else {
          routerParams.name = 'PCDocumentSearch'
        }
      } else if (documentLogic.state.tab === 2) {
        if (store.state.common.device === 'sp') {
          routerParams.name = 'SPDocumentList'
        } else {
          routerParams.name = 'PCDocumentList'
        }
      }

      if (router.currentRoute.name === routerParams.name) {
        // 現在のURLと遷移先のURLが同じ場合は遷移しない
      } else {
        router.push(routerParams)
      }
    },

    clickBookmark: () => {
      if (documentLogic.data.detail.bookmarks_id === 0) {
        const params = {
          type: 1, // bookmark
          id: documentLogic.data.detail.id,
          key: 'pj'
        }
        apiBookmark.add(params)
          .then(response => {
            const data = response.data

            if (data.status === 0) {
              // 詳細表示データbookmarks_id更新処理
              documentLogic.data.detail.bookmarks_id = data.after_id
              store.dispatch('common/showToast', { text: 'ブックマークに保存しました。' })
            } else {
              // エラー処理
              const payload = {
                title: 'エラー',
                text: data.error_message
              }
              store.dispatch('common/showNotice', payload)
            }
          })
      } else {
        const params = {
          // type: 1, // bookmark
          id: documentLogic.data.detail.bookmarks_id
          // key: 'knowledge'
        }
        apiBookmark.remove(params)
          .then(response => {
            const data = response.data
            if (data.status === 0) {
              // 詳細表示データbookmarks_id更新処理
              documentLogic.data.detail.bookmarks_id = 0
              store.dispatch('common/showToast', { text: 'ブックマークから削除しました。' })
            } else {
              // エラー処理
              const payload = {
                title: 'エラー',
                text: data.error_message
              }
              store.dispatch('common/showNotice', payload)
            }
          })
      }
    },

    clickFilterTheme: (payload) => {
      store.dispatch('precedent/setThemeId', payload.theme_id)
      store.dispatch('precedent/setThemeName', payload.title)

      // テーマデータの取得
      const detailIds = [payload.theme_id]
      getPrecedentDetail(detailIds, 'theme')

      documentLogic.state.isFilteredTheme = true
    },

    clickViewLater: (payload) => {
      const resultIndex = _.findIndex(documentLogic.data.list, list => list.id === payload.id)
      if (resultIndex !== -1) {
        documentLogic.data.list[resultIndex].after_id = payload.afterId
      }
    },

    changeDetailURL: (detailId) => {
      const routerParams = {params: { detailId }}
      if (documentLogic.state.tab === 1) {
        if (store.state.common.device === 'sp') {
          routerParams.name = 'SPDocumentSearchDetail'
        } else {
          routerParams.name = 'PCDocumentSearchDetail'
        }
      } else if (documentLogic.state.tab === 2) {
        if (store.state.common.device === 'sp') {
          routerParams.name = 'SPDocumentListDetail'
        } else {
          routerParams.name = 'PCDocumentListDetail'
        }
      }

      if (router.currentRoute.name === routerParams.name && Number(router.currentRoute.params.detailId) === routerParams.params.detailId) {
        // 現在のURLと遷移先のURLが同じ場合は遷移しない
      } else {
        router.push(routerParams)
      }
    },

    getDetaiPage: (detailId) => {
      documentLogic.state.isDetailLoading = true

      // idから詳細取得処理を実行
      const params = { qa_id: detailId }
      apiPrecedent.detail(params)
        .then(response => {
          const { data } = response
          if (data.status === 0) {
            // タブ情報の更新
            if (router.currentRoute.name.includes('DocumentSearch')) {
              store.dispatch('precedent/setTab', 1) // 1：検索タブ、2：目次タブ
            } else {
              store.dispatch('precedent/setTab', 2) // 1：検索タブ、2：目次タブ
            }

            // 書籍の更新があった場合、storeの値を更新してトップに戻る
            // 関連要旨・参照取得処理の場合は処理しない
            if (String(detailId) !== String(data.qa_id)) {
              // storeの値を更新
              store.dispatch('precedent/setBookRevisionId', data.book_revision_id)
              store.dispatch('precedent/clearTheme')
              store.dispatch('precedent/setDetailId', Number(data.qa_id))

              // 更新しますモーダルを表示して情報を復元
              documentLogic.showUpdateError()
            }

            // 初回アクセス時等、テーマIDの判別や書籍の判別を行いたい時はisFirstフラグをtrueにする
            if (documentLogic.state.isFirst) {
              // テーマ情報更新
              store.dispatch('precedent/setThemeId', data.contents[0].theme_id)
              store.dispatch('precedent/setThemeName', data.contents[0].title)

              // 書籍情報更新(リビジョンID以外は書籍セレクタをセットするタイミングで更新)
              store.dispatch('precedent/setBookRevisionId', data.book_revision_id)
              documentLogic.state.isRevisionFixed = true

              documentLogic.state.isFirst = false
            }

            // 表示用にデータを加工
            const updatedResponse = updatePrecedentData(data.contents)
            documentLogic.data.detail = updatedResponse[0]

            documentLogic.state.isDetailLoading = false

          } else {
            // 現在のリビジョンIDと再度PJ001を叩いて取得したリビジョンIDを比較し更新があるか確認しモーダルを表示
            documentLogic.checkRevisionId(data.error_message)

            // ローディング処理
            documentLogic.state.isDetailLoading = false
            documentLogic.state.isMasterLoading = false
          }
        })
    },

    // 一覧エリアのリサイズ監視(PCのみ)
    checkResizeResult: () => {
      const element = context.refs.resultList

      if (element) {
        const resizeObserver = new ResizeObserver((entries) => {
          for (const entry of entries) {
            if (entry.target === element) {
              documentLogic.scrollResult()
            }
          }
        })
        resizeObserver.observe(element)
      }
    },
    // 一覧エリアをカレント表示の要素までスクロール
    scrollResult: () => {
      // カレント要素を取得
      const currentItem = context.refs.resultList ? context.refs.resultList.querySelector('.is-current:not(.document-pager-item)') : undefined

      // カレントまでスクロール
      if (currentItem) {
        context.refs.resultList.scrollTop = currentItem.offsetTop - 100
      }
    },
    // storeに保存されているリビジョンと新たにPJ/001から取得したリビジョンIDが一緒か確認する
    checkRevisionId: async (errorText) => {
      if (errorText === '対象情報は閲覧できません。') {
        const params = {}
        await apiPrecedent.getBook(params)
          .then(response => {
            const { data } = response
            if (data.status === 0 && data.books.length >= 1) {
              const result = _.find(data.books, { book_id: documentLogic.state.bookId})
              
              if (result && result.book_revision_id !== documentLogic.state.bookRevisionId) {
                // storeのdetailとthemeの情報を削除しトップへ遷移
                store.dispatch('precedent/setBookRevisionId', result.book_revision_id)
                store.dispatch('precedent/clearTheme')
                store.dispatch('precedent/clearDetail')

                // 更新しますモーダルを表示して情報を復元
                documentLogic.showUpdateError()
              } else {
                // リビジョンIDが一致していて書籍の更新はないので、そのままエラーを表示
                documentLogic.showDocumentError(errorText)
              }
            } else {
              documentLogic.showDocumentError(errorText)
            }
          })
      } else {
        documentLogic.showDocumentError(errorText)
      }
    },

    // storeの値を削除し、エラーを表示
    showDocumentError: (errorText) => {
      const payload = {
        title: 'エラー',
        text: errorText
      }
      store.dispatch('common/showNotice', payload)
      store.dispatch('precedent/clearAll')
    },

    // 資料更新モーダルを表示して閉じた後に情報を復元
    showUpdateError: () => {
      const routerFunc = () => {
        store.dispatch('common/hideNotice')
        router.push({name: 'DocumentTop'})
      }
      const payload = {
        title: 'エラー',
        text: '資料が更新されました。情報を再取得します。',
        button1: {
          action: routerFunc
        },
        buttonClose: {
          action: routerFunc
        }
      }
      store.dispatch('common/showNotice', payload)
    }
  })

  // 渡されたdetailIdsから資料詳細の情報を取得
  // type:themeは目次側のテーマ一覧取得。relatedとreferenceはテーマや検索一覧の関連要旨・参照を取得
  const getPrecedentDetail = (detailIds, type, index = -1) => {
    // 初期化
    if (type === 'theme') {
      documentLogic.state.isListLoading = true
      documentLogic.data.list = []
    }

    const result = detailIds.map(async (detailId) => {
      // idから詳細取得処理を実行
      const params = {
        qa_id: detailId
      }
      const detailData = await apiPrecedent.detail(params)
        .then(response => {
          const { data } = response

          // テーマ名の変更
          if (type === 'theme') store.dispatch('precedent/setThemeName', data.title)

          if (data.status === 0) {
            // タブ情報の更新
            if (router.currentRoute.name.includes('DocumentSearch')) {
              store.dispatch('precedent/setTab', 1) // 1：検索タブ、2：目次タブ
            } else {
              store.dispatch('precedent/setTab', 2) // 1：検索タブ、2：目次タブ
            }

            // 書籍の更新があった場合、storeの値を更新してトップに戻る
            // 関連要旨・参照取得処理の場合は処理しない
            if (type === 'theme' && String(detailId) !== String(data.qa_id)) {
              // storeの値を更新
              store.dispatch('precedent/setBookRevisionId', data.book_revision_id)
              store.dispatch('precedent/setThemeId', Number(data.qa_id))
              store.dispatch('precedent/clearDetail')

              // 更新しますモーダルを表示して情報を復元
              documentLogic.showUpdateError()
              return []
            }

            // 初回アクセス時等、テーマIDの判別や書籍の判別を行いたい時はisFirstフラグをtrueにする
            if (documentLogic.state.isFirst) {
              store.dispatch('precedent/setThemeId', data.contents[0].theme_id)
              store.dispatch('precedent/setThemeName', data.contents[0].title)
              store.dispatch('precedent/setDetailId', 0)

              // 書籍情報更新(リビジョンID以外は書籍セレクタをセットするタイミングで更新)
              store.dispatch('precedent/setBookRevisionId', data.book_revision_id)
              documentLogic.state.isRevisionFixed = true

              documentLogic.state.isFirst = false
            }

            return data.contents
          } else {
            // 現在のリビジョンIDと再度PJ001を叩いて取得したリビジョンIDを比較し更新があるか確認しモーダルを表示
            documentLogic.checkRevisionId(data.error_message)

            // ローディング処理
            if (type === 'theme') {
              documentLogic.state.isListLoading = false
              documentLogic.state.isMasterLoading = false
            }

            return []
          }
        })

      return detailData
    })

    Promise.all(result)
      .then((result) => {
        // 表示用にデータを加工
        const updatedResponse = updatePrecedentData(result.flat())
        if (type === 'theme') {
          documentLogic.data.list = updatedResponse
        } else if (index >= 0) { // related,reference
          if (type === 'related') {
            Vue.set(documentLogic.data.list[index], 'relatedItems', updatedResponse)
          } else if (type === 'reference') {
            Vue.set(documentLogic.data.list[index], 'referenceItems', updatedResponse)
          }
        }
      })
      .finally(() => {
        if (type === 'theme') documentLogic.state.isListLoading = false
      })
  }

  // 資料詳細の情報を加工
  const updatePrecedentData = (detailData, apiType = 'detail') => {
    const updatedData = detailData.map(item => {
      // 要旨がある場合は要旨を、ない場合は内容のhtmlを加工して110字に丸める
      const maxLength = 110
      let textContents = ''

      if (apiType === 'detail') {
        // 要旨がなければ内容を優先
        if (item.sub_text) {
          textContents = item.sub_text
        } else {
          textContents = item.text
        }
      } else if (apiType === 'search') {
        // ハイライト付き要旨or要旨
        textContents = item.text
      }
      const dummyElement = document.createElement('div')
      dummyElement.innerHTML = textContents

      if (apiType === 'detail') {
        // テキストを抜き出し
        const shortenedText = dummyElement.textContent

        // // 110文字を超えるか判定し値を詰める
        if (shortenedText.length > maxLength) {
          item.shortenedText = shortenedText.slice(0, maxLength) + '…'
        } else {
          item.shortenedText = shortenedText
        }
      } else if (apiType === 'search') {
        const fuzzyElements = dummyElement.querySelectorAll('em.fuzzy')
        const matchedElements = dummyElement.querySelectorAll('em.matched')

        // dom保持フラグ
        const shouldKeep = fuzzyElements.length > 0 || matchedElements.length > 0

        // ハイライトがない場合はhtml全て削除
        if (shouldKeep === false) {
          // テキストを抜き出し
          const shortenedText = dummyElement.textContent

          // // 110文字を超えるか判定し値を詰める
          if (shortenedText.length > maxLength) {
            item.shortenedText = shortenedText.slice(0, maxLength) + '…'
          } else {
            item.shortenedText = shortenedText
          }

        // ハイライトがある場合はハイライトdomを文字数に含めず110文字で切る
        } else {
          let displayDom = ''
          let count = 0
          let isInTag = false

          for (let i = 0; i < textContents.length; i++) {
            const char = textContents[i]

            // dom開始を判定
            if (char === '<') isInTag = true

            // dom内ではない時にカウントアップ
            if (!isInTag) count++

            displayDom += char

            // dom終了を判定
            if (char === '>') isInTag = false

            if (count >= maxLength) {
              displayDom += '…'
              break
            }
          }

          item.shortenedText = displayDom
        }
      }

      // 日付を和暦に変換
      if (item.date === null) {
        item.date = undefined
      } else {
        item.date = common.convertToJapaneseEra(item.date)
      }

      // カレント表示
      if (props.detailId === item.id) {
        item.isCurrent = true
      } else {
        item.isCurrent = false
      }

      return item
    })

    // 最後にマスターデータを使って加工
    if (documentLogic.state.isMasterLoading === false) {
      return updatePrecedentDataWithMaster(updatedData)
    } else {
      return updatedData
    }
  }

  // 資料詳細の情報をマスターデータを使って加工
  const updatePrecedentDataWithMaster = (detailData) => {
    // マスター取得APIから分類・発翰・裁判所データを取得
    const typeData = documentLogic.data.bookTypes
    const hakkanData = documentLogic.data.hakkan
    const saibansyoData = documentLogic.data.saibansyo

    const updatedData = detailData.map(item => {
      // 分類・発翰・裁判所データと照合しデータを追加
      const resultType = _.find(typeData, { code: String(item.classification)})
      const resultHakkan = _.find(hakkanData, { code: String(item.hakkan)})
      const resultSaibansyo = _.find(saibansyoData, { code: String(item.court)})

      item.typeText = resultType ? resultType.value3 : ''
      // value3の分類グループとは別に分類を置換　typeTextは処理分岐に使っているため別変数名を定義
      item.typeMainText = resultType ? resultType.value2 : ''
      item.hakkanText = resultHakkan ? resultHakkan.value2 : ''
      item.saibansyoText = resultSaibansyo ? resultSaibansyo.value2 : ''

      return item
    })
    return updatedData
  }

  return {
    documentLogic,
    getPrecedentDetail,
    updatePrecedentData
  }
}
